import type { EmpresaDetailed, Partner } from '@/types'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { formatCurrency } from '@/utils/format-currency'
import { formatDate } from '@/utils/format-date'
import { Search, Users } from 'lucide-react'
import api from '@/services/api'
import { toast } from 'sonner'

interface EmpresaInfoTabProps {
  empresa: EmpresaDetailed
  editing?: boolean
  editData?: Partial<EmpresaDetailed>
  onEditDataChange?: (data: Partial<EmpresaDetailed>) => void
  partners?: Partner[]
}

export function EmpresaInfoTab({ empresa, editing, editData, onEditDataChange, partners }: EmpresaInfoTabProps) {
  const fields = [
    { label: 'Razão Social', key: 'razaoSocial', value: empresa.razaoSocial ?? '—' },
    { label: 'Nome Fantasia', key: 'nomeFantasia', value: empresa.nomeFantasia ?? empresa.nome ?? '—' },
    { label: 'CNPJ', key: 'cnpj', value: empresa.cnpj ?? '—' },
    { label: 'Contato Principal', key: 'contato', value: empresa.contato ?? '—' },
    { label: 'Cargo', key: 'cargo', value: empresa.cargo ?? '—' },
    { label: 'E-mail', key: 'email', value: empresa.email ?? '—' },
    { label: 'Telefone', key: 'phone', value: empresa.phone ?? empresa.telefone ?? '—' },
    { label: 'Website', key: 'website', value: empresa.website ?? empresa.site ?? '—' },
    { label: 'Segmento', key: 'segmento', value: empresa.segmento ?? '—' },
    { label: 'Plataforma', key: 'plataforma', value: empresa.plataforma ?? '—' },
    { label: 'Modelo', key: 'modelo', value: empresa.modelo ?? '—' },
    { label: 'Faturamento', key: 'faturamento', value: empresa.faturamento ?? '—' },
    { label: 'Tráfego', key: 'trafego', value: empresa.trafego ?? '—' },
    {
      label: 'Ticket Médio',
      key: 'ticketMedio',
      value:
        typeof empresa.ticketMedio === 'number' && Number.isFinite(empresa.ticketMedio)
          ? formatCurrency(empresa.ticketMedio)
          : '—',
    },
  ]

  const addressFields = [
    { label: 'Endereço', key: 'endereco', value: empresa.endereco ?? empresa.address ?? '—' },
    { label: 'CEP', key: 'cep', value: empresa.cep ?? '—' },
    { label: 'Cidade', key: 'cidade', value: empresa.cidade ?? '—' },
    { label: 'Estado', key: 'estado', value: empresa.estado ?? '—' },
  ]

  const handleBuscaCNPJ = async () => {
    const cnpj = (editData?.cnpj || '').replace(/\D/g, '')
    if (!cnpj || cnpj.length !== 14) {
      toast.error('CNPJ inválido. Deve ter 14 dígitos.')
      return
    }
    const toastId = toast.loading('Buscando dados do CNPJ...')
    try {
      const resp = await api.get(`/empresas/cnpj/${cnpj}?source=brasilapi`)
      const data = resp.data
      
      if (onEditDataChange && editData) {
        onEditDataChange({
          ...editData,
          razaoSocial: data.razaoSocial || editData.razaoSocial,
          nomeFantasia: data.nomeFantasia || editData.nomeFantasia,
          nome: editData.nome ? editData.nome : (data.nomeFantasia || data.razaoSocial),
          cep: data.cep || editData.cep,
          cidade: data.cidade || editData.cidade,
          estado: data.estado?.toUpperCase() || editData.estado,
          endereco: data.endereco || editData.endereco,
          telefone: editData.telefone || data.telefone || '',
          email: editData.email || data.email || '',
        })
      }
      toast.success('Dados encontrados! Campos preenchidos.', { id: toastId })
    } catch (err: any) {
      console.error(err)
      toast.error('Erro ao buscar CNPJ: ' + (err.response?.data?.message || err.message), { id: toastId })
    }
  }

  const renderField = (field: { label: string; key: string; value: string | number }) => {
    if (editing && onEditDataChange && editData) {
      const fieldValue = editData[field.key as keyof EmpresaDetailed] ?? ''
      
      if (field.key === 'cnpj') {
        return (
          <div className='flex gap-2 w-full'>
            <Input
              value={String(fieldValue)}
              onChange={(e) => onEditDataChange({ ...editData, [field.key]: e.target.value })}
              className='text-sm flex-1'
              placeholder='00.000.000/0000-00'
              maxLength={18}
            />
            <Button
              type='button'
              variant='secondary'
              size='sm'
              onClick={handleBuscaCNPJ}
              icon={<Search size={14} />}
              title='Buscar na Receita'
            />
          </div>
        )
      }

      return (
        <Input
          value={String(fieldValue)}
          onChange={(e) => onEditDataChange({ ...editData, [field.key]: e.target.value })}
          className='text-sm w-full'
        />
      )
    }
    return (
      <p
        className='text-[var(--font-color-body)]'
        style={{ fontSize: 'var(--font-size-sm)' }}
      >
        {field.value}
      </p>
    )
  }

  return (
    <div className='space-y-6'>
      <div>
        <h4
          className='text-[var(--font-color-label)] uppercase tracking-wider font-semibold mb-3 border-b border-[var(--glass-border)] pb-1.5'
          style={{ fontSize: 'var(--font-size-sm)' }}
        >
          Dados Cadastrais
        </h4>
        <div className='grid grid-cols-2 gap-3'>
          {fields.map((field) => (
            <div key={field.label}>
              <p
                className='text-[var(--font-color-label)] uppercase tracking-wider font-medium mb-0.5'
                style={{ fontSize: 'var(--font-size-label)' }}
              >
                {field.label}
              </p>
              {renderField(field)}
            </div>
          ))}
        </div>
      </div>

      <div className='border-t border-[var(--glass-border)] pt-4'>
        <h4
          className='text-[var(--font-color-label)] uppercase tracking-wider font-semibold mb-3 border-b border-[var(--glass-border)] pb-1.5'
          style={{ fontSize: 'var(--font-size-sm)' }}
        >
          Parceria
        </h4>
        {editing && onEditDataChange && editData ? (
          <div className='w-full'>
            <select
              value={editData.parceiroId || ''}
              onChange={(e) => onEditDataChange({ ...editData, parceiroId: e.target.value || undefined })}
              className='w-full bg-[var(--bg-input)] border border-[var(--glass-border)] rounded-md px-3 py-2 text-sm text-[var(--font-color-body)]'
            >
              <option value=''>Sem Parceria</option>
              {(partners || []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}{p.empresa ? ` - ${p.empresa}` : ''}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className='flex items-center gap-2 p-3 bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-lg w-fit min-w-[280px]'>
            <Users size={16} className='text-[var(--brand-primary)]' />
            <span className='text-sm text-[var(--font-color-body)]'>
              {empresa.parceiro
                ? `${empresa.parceiro.nome}${empresa.parceiro.empresa ? ` - ${empresa.parceiro.empresa}` : ''}`
                : 'Nenhuma parceria ativa'}
            </span>
          </div>
        )}
      </div>

      <div className='border-t border-[var(--glass-border)] pt-4'>
        <h4
          className='text-[var(--font-color-label)] uppercase tracking-wider font-semibold mb-3 border-b border-[var(--glass-border)] pb-1.5'
          style={{ fontSize: 'var(--font-size-sm)' }}
        >
          Endereço
        </h4>
        <div className='grid grid-cols-2 gap-3'>
          {addressFields.map((field) => (
            <div key={field.label}>
              <p
                className='text-[var(--font-color-label)] uppercase tracking-wider font-medium mb-0.5'
                style={{ fontSize: 'var(--font-size-label)' }}
              >
                {field.label}
              </p>
              {renderField(field)}
            </div>
          ))}
        </div>
      </div>

      <div className='border-t border-[var(--glass-border)] pt-4'>
        <p
          className='text-[var(--font-color-muted)]'
          style={{ fontSize: 'var(--font-size-xs)' }}
        >
          Criado em {formatDate(empresa.createdAt)} · Atualizado em {formatDate(empresa.updatedAt)}
        </p>
      </div>
    </div>
  )
}
